import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Vendor → Propose a multi-engineer crew (ADR-019, Commit 3).
 *
 * Rendered inside VendorJob.tsx BEFORE accept, when the per-tenant
 * `crew_assignment` flag is on. The vendor builds a crew of 1 Lead + up
 * to 4 Members and submits for SDA approval. Coordinator's SDA approves;
 * each member then gets their own no-login link (same flow as a
 * coordinator-built crew).
 *
 * No-login, token-as-auth — the URL token IS the credential. The vendor
 * cannot approve / promote / revoke from here; those stay with the
 * coordinator + SDA per ADR-019.
 */
import { useCallback, useEffect, useState } from 'react';
import { t } from '@/lib/i18n';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/Toast';
import { ApiError, publicGet, publicPost } from '@/lib/api';
import { inputCls, ResumeAttachInput } from './VendorVisitShared';
const STATUS_LABEL = {
    proposed: 'Proposed',
    pending_approval: 'Awaiting SDA',
    active: 'Active',
    completed: 'Completed',
    declined: 'Declined',
    revoked: 'Revoked',
    expired: 'Expired',
};
export function VendorCrewBlock({ token, busy, run, requiredSize, crewMode, }) {
    const isFlatNoLead = crewMode === 'flat_no_lead';
    const toast = useToast();
    const [roster, setRoster] = useState(null);
    const [showAdd, setShowAdd] = useState(false);
    // form
    // Mode D — every entry is a Member from the start (no Lead role).
    const [role, setRole] = useState(isFlatNoLead ? 'member' : 'lead');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [resume, setResume] = useState(null);
    const load = useCallback(async () => {
        try {
            setRoster(await publicGet(`/vendor/wo/${token}/crew`));
        }
        catch (e) {
            // 404 = flag off → just don't render anything
            if (e.status !== 404) {
                toast.error(t('Could not load crew'), e.message);
            }
        }
    }, [token, toast]);
    useEffect(() => {
        void load();
    }, [load]);
    if (!roster)
        return null;
    const inFlight = roster.members.filter((m) => ['proposed', 'pending_approval', 'active'].includes(m.status));
    const lead = inFlight.find((m) => m.role === 'lead');
    const others = inFlight.filter((m) => m !== lead);
    const ordered = lead ? [lead, ...others] : others;
    const proposedCount = inFlight.filter((m) => m.status === 'proposed').length;
    // When the SDA committed to a crew size, that's the cap; otherwise the
    // global max of 5 applies.
    const required = requiredSize && requiredSize > 1 ? requiredSize : null;
    const cap = required ?? 5;
    const fullSlots = inFlight.length >= cap;
    const hasLead = !!lead;
    // Submit-gate: with a required size, must have EXACTLY that many active
    // members (matches the backend's submit_for_approval check) AND a Lead.
    const submitBlocked = required != null && inFlight.length !== required;
    const addMember = async () => {
        await publicPost(`/vendor/wo/${token}/crew/members`, {
            member_kind: 'vendor_delegated_engineer',
            role,
            display_name: name.trim(),
            email: email.trim(),
            phone: phone.trim() || null,
            resume_blob_name: resume?.blob_name ?? null,
            resume_filename: resume?.filename ?? null,
        });
        setName('');
        setEmail('');
        setPhone('');
        setResume(null);
        // Mode D — always Member (no Lead role exists). Other modes: same logic.
        setRole(isFlatNoLead ? 'member' : hasLead || role === 'member' ? 'member' : 'lead');
        setShowAdd(false);
        await load();
    };
    const submit = async () => {
        await publicPost(`/vendor/wo/${token}/crew/submit`, {});
        await load();
    };
    return (_jsxs("div", { className: "space-y-3 rounded-2xl border border-indigo-100 bg-indigo-50/40 p-4 dark:border-indigo-900/50 dark:bg-indigo-950/20", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { children: [_jsx("div", { className: "text-sm font-semibold text-slate-800 dark:text-slate-100", children: t('Propose a crew') }), _jsx("p", { className: "text-xs text-slate-500 dark:text-slate-400", children: required
                                    ? `${inFlight.length} of ${required} added${submitBlocked
                                        ? ` — ${required - inFlight.length > 0 ? 'add' : 'remove'} ${Math.abs(required - inFlight.length)} ${Math.abs(required - inFlight.length) === 1 ? 'engineer' : 'engineers'} before submitting`
                                        : ' — ready to submit'}.`
                                    : "Up to 5 engineers (1 Lead + 0–4 Members). After you submit, your dispatcher's SDA approves and each engineer gets their own no-login link." })] }), !fullSlots && (_jsx(Button, { size: "sm", variant: "ghost", disabled: busy, onClick: () => {
                            setRole(isFlatNoLead ? 'member' : hasLead ? 'member' : 'lead');
                            setShowAdd(true);
                        }, children: "+ Add member" }))] }), inFlight.length === 0 ? (_jsx("div", { className: "rounded-lg border border-dashed border-slate-300 p-3 text-center text-xs text-slate-500 dark:border-slate-700", children: isFlatNoLead
                    ? `No crew yet. Add ${requiredSize ?? 2}–5 equal engineers (no Lead — every engineer logs their own time).`
                    : 'No crew yet. Add a Lead, then up to 4 Members.' })) : (_jsx("ul", { className: "space-y-1 text-sm", children: ordered.map((m) => (_jsxs("li", { className: "flex items-center justify-between rounded-md bg-white px-3 py-2 dark:bg-slate-900", children: [_jsxs("span", { children: [_jsx("b", { children: m.display_name }), ' ', m.role === 'lead' && (_jsx("span", { className: "ml-1 text-[10px] uppercase text-indigo-600 dark:text-indigo-400", children: t('Lead') })), _jsx("span", { className: "ml-2 text-[11px] text-slate-500", children: m.email })] }), _jsx("span", { className: "text-[11px] text-slate-400", children: STATUS_LABEL[m.status] })] }, m.id))) })), showAdd && (_jsxs("div", { className: "space-y-2 rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900", children: [!isFlatNoLead && (_jsxs("select", { className: inputCls, value: role, onChange: (e) => setRole(e.target.value), children: [_jsxs("option", { value: "lead", disabled: hasLead, children: ["Lead ", hasLead && '(taken)'] }), _jsx("option", { value: "member", children: "Member" })] })), _jsx("input", { className: inputCls, placeholder: t('Engineer name'), value: name, onChange: (e) => setName(e.target.value) }), _jsx("input", { className: inputCls, type: "email", placeholder: t('Engineer email'), value: email, onChange: (e) => setEmail(e.target.value) }), _jsx("input", { className: inputCls, placeholder: t('Phone (optional)'), value: phone, onChange: (e) => setPhone(e.target.value) }), _jsx(ResumeAttachInput, { mint: (filename, contentType) => publicPost(`/vendor/wo/${token}/resume-upload`, { filename, content_type: contentType }), value: resume, onChange: setResume, disabled: busy }), _jsxs("div", { className: "flex justify-end gap-2", children: [_jsx(Button, { size: "sm", variant: "ghost", onClick: () => setShowAdd(false), disabled: busy, children: t('Cancel') }), _jsx(Button, { size: "sm", disabled: busy || name.trim().length < 1 || email.trim().length < 3, onClick: () => run(addMember, 'Member added'), children: t('Add to crew') })] })] })), proposedCount > 0 && (hasLead || isFlatNoLead) && (_jsxs(Button, { className: "w-full", disabled: busy || submitBlocked, onClick: () => run(submit, 'Submitted for approval'), title: submitBlocked
                    ? `The service desk requested ${required} engineers — adjust the roster first`
                    : undefined, children: ["Submit ", proposedCount, " for approval"] })), inFlight.some((m) => m.status === 'pending_approval') && (_jsx("div", { className: "rounded-md bg-amber-50 p-2 text-center text-xs text-amber-700 dark:bg-amber-950/30 dark:text-amber-300", children: "\u23F3 Awaiting your dispatcher's approval. Engineer links go out automatically once approved." }))] }));
}
